import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useTheme } from "@/contexts/ThemeContext";
import { toast } from "sonner";
import { Storefront, MoonStars, Sun } from "@phosphor-icons/react";
import ImageUpload from "@/components/ImageUpload";

export default function Settings() {
  const { theme, toggle } = useTheme();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings", {
        store_name: s.store_name || "",
        gst_number: s.gst_number || null,
        address: s.address || null,
        phone: s.phone || null,
        email: s.email || null,
        logo_url: s.logo_url || null,
        invoice_prefix: s.invoice_prefix || "INV",
        invoice_footer: s.invoice_footer || "",
        default_theme: s.default_theme || "dark",
        loyalty_points_per_100: Number(s.loyalty_points_per_100) || 1,
        default_gst_percent: Number(s.default_gst_percent) || 3,
      });
      setS(data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  if (!s) return <div className="p-10 text-muted-foreground">Loading...</div>;
  const f = (k) => ({
    value: s[k] ?? "",
    onChange: (e) => setS({ ...s, [k]: e.target.value }),
  });

  return (
    <div
      className="p-6 md:p-10 space-y-6 max-w-5xl"
      data-testid="settings-page"
    >
      <header>
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          Workspace
        </div>
        <h1 className="font-display text-3xl md:text-4xl tracking-tight mt-1">
          Settings
        </h1>
      </header>

      <section className="rounded-xl border border-border bg-card/40 p-6 space-y-5">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-primary/10 border border-primary/30 grid place-items-center text-gold">
            <Storefront size={18} weight="duotone" />
          </div>
          <div>
            <h2 className="font-display text-xl">Store branding</h2>
            <p className="text-xs text-muted-foreground">
              Used on every invoice and receipt.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            ["store_name", "Store name"],
            ["gst_number", "GST number"],
            ["phone", "Phone"],
            ["email", "Email"],
            ["invoice_prefix", "Invoice prefix"],
          ].map(([k, l]) => (
            <div key={k}>
              <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                {l}
              </Label>
              <Input
                data-testid={`settings-${k}`}
                className="mt-1.5 bg-background border-border"
                {...f(k)}
              />
            </div>
          ))}
          <div className="md:col-span-2">
            <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
              Address
            </Label>
            <Input
              className="mt-1.5 bg-background border-border"
              {...f("address")}
            />
          </div>
          <div className="md:col-span-2">
            <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
              Invoice footer
            </Label>
            <Input
              className="mt-1.5 bg-background border-border"
              {...f("invoice_footer")}
            />
          </div>
          <div className="md:col-span-2">
            <ImageUpload
              testid="settings-logo-upload"
              label="Store logo (shown on invoices)"
              value={s.logo_url}
              onChange={(url) => setS({ ...s, logo_url: url })}
            />
          </div>
        </div>
      </section>

      <section className="rounded-xl border border-border bg-card/40 p-6 space-y-5">
        <h2 className="font-display text-xl">Operational</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
              Default GST %
            </Label>
            <Input
              type="number"
              step="0.01"
              className="mt-1.5 bg-background border-border tabular-nums"
              {...f("default_gst_percent")}
            />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
              Loyalty points per ₹100 spent
            </Label>
            <Input
              type="number"
              step="0.01"
              className="mt-1.5 bg-background border-border tabular-nums"
              {...f("loyalty_points_per_100")}
            />
          </div>
        </div>
      </section>

      <section className="rounded-xl border border-border bg-card/40 p-6 space-y-4">
        <h2 className="font-display text-xl">Appearance</h2>
        <div className="flex items-center justify-between p-4 rounded-lg border border-border">
          <div className="flex items-center gap-3">
            {theme === "dark" ? (
              <MoonStars size={20} className="text-gold" />
            ) : (
              <Sun size={20} className="text-gold" />
            )}
            <div>
              <div className="font-medium">
                {theme === "dark" ? "Dark mode" : "Light mode"}
              </div>
              <div className="text-xs text-muted-foreground">
                Persisted on this device.
              </div>
            </div>
          </div>
          <Switch
            data-testid="theme-toggle"
            checked={theme === "light"}
            onCheckedChange={toggle}
          />
        </div>
      </section>

      <div>
        <Button
          data-testid="settings-save"
          onClick={save}
          disabled={saving}
          loading={saving}
          className="bg-primary text-primary-foreground hover:bg-primary/90"
        >
          {saving ? "Saving..." : "Save settings"}
        </Button>
      </div>
    </div>
  );
}
